// HRAddPersonModal — modal pro vytvoření nové osoby (owner only).
//
// Workflow:
//   1. Owner vyplní: display_name (povinné), email, telefon, pozice, typ_spoluprace,
//      zacatek_spoluprace, is_trener (auto dle pozice).
//   2. Klik "Zkontrolovat" → POST bez confirm → server vrátí dry-run preview
//      a dedup check (vrátí 409 + duplicates pokud osoba už existuje).
//   3. Po preview → klik "Vytvořit" → POST s confirm "ANO_VYTVORIT".
//   4. onCreated(newId) callback.

import React, { useState } from "react";
import { Plus, UserPlus, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { apiFetch } from "../lib/api.js";
import useEscClose from "../lib/useEscClose.js";
import { Modal, HlavickaOkna } from "./ui.jsx";

const POZICE_OPTIONS = ["CEO", "Head Coach Senior", "Head Coach Junior", "Trener", "Nutricni specialista", "Clients Manager", "Sales", "Marketing", "HR", "IT"];
const TRENER_POZICE = new Set(["Trener", "Head Coach Junior", "Head Coach Senior"]);
const TYP_SPOLUPRACE_OPTIONS = ["HPP", "DPP", "DPC", "OSVČ"];

function poziceLabel(p) {
  return p === "Trener" ? "Trenér" : (p === "Nutricni specialista" ? "Nutriční specialista" : p);
}

export default function HRAddPersonModal({ onClose, onCreated }) {
  useEscClose(onClose); // Esc zavre dialog
  const [form, setForm] = useState({
    display_name: "",
    email: "",
    telefon: "",
    pozice: "",
    typ_spoluprace: "",
    zacatek_spoluprace: new Date().toISOString().substring(0, 10),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [previewed, setPreviewed] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // is_trener auto dle pozice
  const isTrener = TRENER_POZICE.has(form.pozice);

  const doSubmit = async (confirm) => {
    setLoading(true);
    setError(null);
    setDuplicates(null);
    try {
      const body = {
        display_name: form.display_name.trim(),
        email: form.email.trim().toLowerCase(),
        telefon: form.telefon.trim(),
        pozice: form.pozice,
        typ_spoluprace: form.typ_spoluprace,
        zacatek_spoluprace: form.zacatek_spoluprace || null,
        is_trener: isTrener,
        ...(confirm ? { confirm: "ANO_VYTVORIT" } : {}),
      };
      const r = await apiFetch("/api/hr-create-person", {
        method: "POST",
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (r.status === 409) {
        setDuplicates(j.duplicates || []);
        setError(j.error || "Osoba pravděpodobně existuje.");
        return;
      }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      if (!confirm) {
        setPreviewed(true);
      } else {
        onCreated && onCreated(j.created?.id);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = form.display_name.trim().length > 0 && form.pozice;

  return (
    <Modal
      open
      onClose={onClose}
      size="sm"
      plnaHlavicka
      header={<HlavickaOkna ikona={UserPlus} nazev="Přidat osobu" onClose={onClose} />}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">Zrušit</button>
          {!previewed ? (
            <button type="button" disabled={!canSubmit || loading} onClick={() => doSubmit(false)} className="btn btn-primary">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Zkontrolovat
            </button>
          ) : (
            <button type="button" disabled={loading} onClick={() => doSubmit(true)} className="btn btn-primary">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Vytvořit
            </button>
          )}
        </>
      }
    >
      <div className="space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              {error}
              {duplicates && duplicates.length > 0 && (
                <div className="mt-2 space-y-1">
                  {duplicates.map((d, i) => (
                    <div key={i} className="text-xs bg-white border border-red-200 rounded p-2">
                      <span className="font-medium">{d.existing.display_name}</span> ({d.existing.email || "bez emailu"})
                      <span className="text-slate-500 ml-2">match: {d.matchedBy}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {previewed && !error && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800">
              <CheckCircle2 className="w-4 h-4 inline mr-2" />
              Žádné duplicity. Klikni "Vytvořit" pro vložení do DB.
            </div>
          )}

          <FieldRow label="Jméno a příjmení *" value={form.display_name} onChange={(v) => set("display_name", v)} />
          <FieldRow label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} />
          <FieldRow label="Telefon" value={form.telefon} onChange={(v) => set("telefon", v)} />

          <div>
            <label className="text-xs text-slate-500 font-medium block mb-1">Pozice *</label>
            <select
              value={form.pozice}
              onChange={(e) => set("pozice", e.target.value)}
              className="field w-full"
            >
              <option value="">Vyber</option>
              {POZICE_OPTIONS.map((p) => <option key={p} value={p}>{poziceLabel(p)}</option>)}
            </select>
            {isTrener && (
              <div className="text-[11px] text-brand-600 mt-1">✓ Označeno jako trenér (is_trener = true)</div>
            )}
          </div>

          <div>
            <label className="text-xs text-slate-500 font-medium block mb-1">Typ spolupráce</label>
            <select
              value={form.typ_spoluprace}
              onChange={(e) => set("typ_spoluprace", e.target.value)}
              className="field w-full"
            >
              <option value="">—</option>
              {TYP_SPOLUPRACE_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <FieldRow label="Začátek spolupráce" type="date" value={form.zacatek_spoluprace} onChange={(v) => set("zacatek_spoluprace", v)} />

          <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
            Po vytvoření můžeš v detailu doplnit fakturační údaje, dokumenty, fotku, slack ID a další.
          </div>
        </div>

    </Modal>
  );
}

function FieldRow({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="text-xs text-slate-500 font-medium block mb-1">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="field w-full"
      />
    </div>
  );
}
